import { computed, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company } from '@app/shared/types/models/company/company.model';
import { Cooperation } from '@app/shared/types/models/cooperation/cooperation.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { AuthService } from '../auth.service';
import { CompaniesDataService } from '../companies-data.service';
import { CooperationsDataService } from '../cooperations-data.service';
import { NotificationsService } from '../notifications.service';

@Injectable({ providedIn: 'root' })
export class ProjectParticipantActionsService {
  private readonly notificationsService = inject(NotificationsService);

  private readonly authService = inject(AuthService);
  private readonly companiesDataService = inject(CompaniesDataService);
  private readonly cooperationsDataService = inject(CooperationsDataService);

  public constructor() {
    this.notificationsService.notifications$.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.handleEvent();
      }
    });
  }

  protected handleEvent(): void {}

  public getCompaniesForProjectParticipants(project: Project): Signal<Company[]> {
    const company = project.company();
    if (company) {
      this.cooperationsDataService.loadOutgoing(company.id);
    }

    const projectCooperations = computed(() => {
      const cooperations = this.cooperationsDataService.getOutgoing(company.id)();

      if (!cooperations?.length) {
        return [];
      }

      const result: Cooperation[] = [];
      cooperations.forEach(c => {
        const cooperationProjects = this.cooperationsDataService.getCooperationProjects(c.id)();
        if (cooperationProjects?.length) {
          if (cooperationProjects.some(cp => cp.project()?.id === project.id)) {
            result.push(c);
          }
        }
      });
      return result;
    });

    return computed(() => {
      const userCompanies = this.authService.currentUser()?.id
        ? this.companiesDataService.getUserCompanies(this.authService.currentUser().id)()
        : [];

      const cooperationCompanies = projectCooperations().map(c => c.receipientCompany());
      const uniqueCompanies = Array.from(new Map(cooperationCompanies.map(c => [c.id, c])).values());

      return Array.from(new Map([...userCompanies, ...uniqueCompanies].map(c => [c.id, c])).values());
    });
  }
}
